export class VirtualAccount {
  id: string;
  provider_id: string;
  va_number: string;
  mill_id: number;
  expire_at: string;
  status: string;
  reference_id: string;
  amount: number;

  constructor(data: any) {
    this.id = data.id;
    this.provider_id = data.provider_id;
    this.va_number = data.va_number;
    this.mill_id = data.mill_id;
    this.expire_at = data.expire_at;
    this.status = data.status;
    this.reference_id = data.reference_id;
    this.amount = data.amount || 0;
  }

  // UI helper methods
  get accountNumber(): string {
    return this.va_number;
  }

  getBankName(providers: PaymentProvider[]): string {
    const provider = providers.find(p => p.id === this.provider_id);
    return provider?.displayName || provider?.legalName || 'Unknown Bank';
  }

  getBankCode(providers: PaymentProvider[]): string {
    const provider = providers.find(p => p.id === this.provider_id);
    return provider?.bankCode || '';
  }

  get accountName(): string {
    return `Virtual Account ${this.id}`;
  }

  get currency(): string {
    return 'IDR';
  }

  get type(): 'VA' | 'Bank Transfer' {
    return 'VA';
  }

  get createdDate(): string {
    return new Date().toISOString().split('T')[0]; // Default to today if not available
  }

  isExpired(): boolean {
    if (!this.expire_at) return false;
    return new Date(this.expire_at) < new Date();
  }

  isActive(): boolean {
    return this.status.toLocaleLowerCase() === 'active';
  }

  static build(data: any): VirtualAccount {
    return new VirtualAccount(data);
  }
}

export class PaymentProvider {
  id: string;
  accepts_va: boolean;
  bankCode: string;
  displayName: string;
  enabled: boolean;
  legalName: string;

  constructor(data: any) {
    this.id = data.id;
    this.accepts_va = data.accepts_va;
    this.bankCode = data.bankCode;
    this.displayName = data.displayName;
    this.enabled = data.enabled;
    this.legalName = data.legalName;
  }

  isAvailable(): boolean {
    return this.enabled && this.accepts_va;
  }

  static build(data: any): PaymentProvider {
    return new PaymentProvider(data);
  }
}

export class PaymentModel {
  virtualAccounts: VirtualAccount[];
  paymentProviders: PaymentProvider[];

  constructor(data: any) {
    this.virtualAccounts = data.va_list?.map((va: any) => VirtualAccount.build(va)) ?? [];
    this.paymentProviders = data.va_providers?.map((provider: any) => PaymentProvider.build(provider)) ?? [];
  }

  getActiveVirtualAccounts(): VirtualAccount[] {
    return this.virtualAccounts.filter(va => va.isActive());
  }

  getAvailableProviders(): PaymentProvider[] {
    return this.paymentProviders.filter(provider => provider.isAvailable());
  }

  getVirtualAccountByProvider(providerId: string): VirtualAccount | undefined {
    return this.virtualAccounts.find(va => va.provider_id === providerId);
  }

  getProviderById(providerId: string): PaymentProvider | undefined {
    return this.paymentProviders.find(provider => provider.id === providerId);
  }

  hasActiveVirtualAccounts(): boolean {
    return this.getActiveVirtualAccounts().length > 0;
  }

  static build(data: any): PaymentModel {
    return new PaymentModel(data);
  }
}
